import { useEffect, useMemo, useState } from 'react';

export interface TypewriterOptions {
  typeMs?: number;
  deleteMs?: number;
  holdMs?: number;
  pauseMs?: number;
  // When false, the animation does not run and the hook returns '' (no timers).
  // Flipping it to true starts typing from the first greeting. Defaults to true.
  enabled?: boolean;
}

const DEFAULTS = { typeMs: 55, deleteMs: 30, holdMs: 3000, pauseMs: 400 };

type Phase = 'typing' | 'deleting' | 'done';
interface State {
  index: number;
  count: number;
  phase: Phase;
}

/**
 * Returns true when the user has requested reduced motion.
 */
const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Typewriter hook. Cycles through `texts`, typing then deleting each, looping
 * forever. A single text types once and stays. Under prefers-reduced-motion the
 * first text is shown statically with no timers.
 *
 * @param texts greeting strings to cycle through
 * @param options optional per-phase timing overrides (ms)
 * @returns the currently visible substring
 */
export function useTypewriter(texts: string[], options: TypewriterOptions = {}): string {
  const timing = { ...DEFAULTS, ...options };
  const enabled = options.enabled ?? true;
  // Stable item list keyed by content so a new-but-equal array prop does not reset.
  const joined = texts.join('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const items = useMemo(() => texts.filter((t) => t && t.trim().length > 0), [joined]);
  const reduced = prefersReducedMotion();

  const [state, setState] = useState<State>({ index: 0, count: 0, phase: 'typing' });

  // Reset to the start whenever the set of greetings changes, or when the
  // animation is (re-)enabled — so it always begins typing from the first one.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setState({ index: 0, count: 0, phase: 'typing' });
  }, [joined, enabled]);

  useEffect(() => {
    if (reduced || !enabled || items.length === 0) {
      return undefined;
    }
    const current = items[state.index] ?? '';
    const single = items.length <= 1;
    let delay: number;
    let next: State;

    switch (state.phase) {
      case 'typing':
        if (state.count < current.length) {
          delay = timing.typeMs;
          next = { ...state, count: state.count + 1 };
        } else {
          delay = timing.holdMs;
          next = { ...state, phase: single ? 'done' : 'deleting' };
        }
        break;
      case 'deleting':
        if (state.count > 0) {
          delay = timing.deleteMs;
          next = { ...state, count: state.count - 1 };
        } else {
          delay = timing.pauseMs;
          next = { index: (state.index + 1) % items.length, count: 0, phase: 'typing' };
        }
        break;
      case 'done':
      default:
        return undefined;
    }

    const id = setTimeout(() => setState(next), delay);
    return () => clearTimeout(id);
  }, [
    state,
    items,
    reduced,
    enabled,
    timing.typeMs,
    timing.deleteMs,
    timing.holdMs,
    timing.pauseMs,
  ]);

  if (reduced) {
    return items[0] ?? '';
  }
  if (!enabled || items.length === 0) {
    return '';
  }
  return (items[state.index] ?? '').slice(0, state.count);
}
